import { useState, useEffect } from 'react';
import { X, Check, Minus, GitCompare, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCompare } from '@/contexts/CompareContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslatedContent } from '@/hooks/useTranslatedContent';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const SUPABASE_URL = 'https://cyllqmkqbdilicnbdxts.supabase.co';

function getLogoUrl(logoPath: string | null): string | null {
  if (!logoPath) return null;
  if (logoPath.startsWith('http')) return logoPath;
  return `${SUPABASE_URL}/storage/v1/object/public/logos/${logoPath}`;
}

// Specialty colors mapping - consistent with SolutionCard and SolutionDetail
const SPECIALTY_COLORS: Record<string, string> = {
  'thoracic': 'tag-thoracic',
  'neuro': 'tag-neuro',
  'cardiac': 'tag-cardiac',
  'musculoskeletal': 'tag-musculoskeletal',
  'abdominal': 'tag-abdominal',
  'breast': 'tag-breast',
  'oncology': 'tag-oncology',
  'vascular': 'tag-vascular',
  'pediatric': 'tag-pediatric',
  'emergency': 'tag-emergency',
};

function getSpecialtyColorClass(specialty: string): string {
  const lowerSpecialty = specialty.toLowerCase();
  for (const [key, value] of Object.entries(SPECIALTY_COLORS)) {
    if (lowerSpecialty.includes(key)) {
      return value;
    }
  }
  return 'bg-muted/50 text-muted-foreground';
}

// Modality colors
function getModalityColorClass(): string {
  return 'bg-primary/10 text-primary border border-primary/20';
}

// Deployment colors
function getDeploymentColorClass(): string {
  return 'bg-secondary/50 text-foreground border border-border';
}

interface CompareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CompareModal({ isOpen, onClose }: CompareModalProps) {
  const { selectedSolutions, removeSolution, clearSolutions } = useCompare();
  const { t } = useLanguage();
  const { translateSpecialty, translateModality, translateDeployment } = useTranslatedContent();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const { data: fullSolutions } = useQuery({
    queryKey: ['compare-solutions', selectedSolutions.map(s => s.id)],
    queryFn: async () => {
      if (selectedSolutions.length === 0) return [];
      const { data, error } = await supabase
        .from('ai_solutions')
        .select('*')
        .in('id', selectedSolutions.map(s => s.id));
      if (error) throw error;
      return data;
    },
    enabled: isOpen && selectedSolutions.length > 0
  });

  if (!isVisible) return null;

  const criteria = [
    { key: 'ai_provider', label: t('compare.provider') },
    { key: 'specialties', label: t('compare.specialties'), isArray: true, translate: translateSpecialty, colorFn: getSpecialtyColorClass },
    { key: 'modalities', label: t('compare.modalities'), isArray: true, translate: translateModality, colorClass: getModalityColorClass() },
    { key: 'has_ce', label: 'CE', isBoolean: true, color: 'emerald' },
    { key: 'has_fda', label: 'FDA', isBoolean: true, color: 'blue' },
    { key: 'has_csa', label: 'CSA', isBoolean: true, color: 'amber' },
    { key: 'has_mhra', label: 'MHRA', isBoolean: true, color: 'purple' },
    { key: 'gdpr_compliant', label: 'GDPR', isBoolean: true },
    { key: 'hipaa_compliant', label: 'HIPAA', isBoolean: true },
    { key: 'iso_certified', label: 'ISO', isBoolean: true },
    { key: 'sensitivity', label: t('compare.sensitivity'), suffix: '%', isMetric: true },
    { key: 'specificity', label: t('compare.specificity'), suffix: '%', isMetric: true },
    { key: 'processing_time', label: t('compare.processingTime'), suffix: 's', isMetric: true },
    { key: 'deployment_options', label: t('compare.deployment'), isArray: true, translate: translateDeployment, colorClass: getDeploymentColorClass() },
  ];

  const getBooleanColor = (color?: string) => {
    switch (color) {
      case 'emerald': return 'bg-emerald-500/20 text-emerald-500';
      case 'blue': return 'bg-blue-500/20 text-blue-500';
      case 'amber': return 'bg-amber-500/20 text-amber-500';
      case 'purple': return 'bg-purple-500/20 text-purple-500';
      default: return 'bg-emerald-500/20 text-emerald-500';
    }
  };

  const getMetricColor = (value: number, key: string) => {
    if (key === 'processing_time') {
      // Lower is better for processing time
      return value <= 5 ? 'text-emerald-500' : value <= 15 ? 'text-amber-500' : 'text-foreground';
    }
    // Higher is better for sensitivity/specificity
    return value >= 90 ? 'text-emerald-500' : value >= 70 ? 'text-amber-500' : 'text-foreground';
  };

  const renderValue = (solution: any, criterion: any) => {
    const value = solution?.[criterion.key];
    
    if (value === null || value === undefined) {
      return <Minus className="w-4 h-4 text-muted-foreground/50" />;
    }
    
    if (criterion.isBoolean) {
      return value ? (
        <div className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${getBooleanColor(criterion.color)}`}>
          <Check className="w-4 h-4" />
        </div>
      ) : (
        <Minus className="w-4 h-4 text-muted-foreground/50" />
      );
    }
    
    if (criterion.isArray && Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-1.5 justify-center">
          {value.slice(0, 3).map((item: string, i: number) => {
            const colorClass = criterion.colorFn 
              ? criterion.colorFn(item) 
              : criterion.colorClass || 'bg-muted text-muted-foreground';
            return (
              <span 
                key={i} 
                className={`text-xs px-2.5 py-1 rounded-full font-medium ${colorClass}`}
              >
                {criterion.translate ? criterion.translate(item) : item}
              </span>
            );
          })}
          {value.length > 3 && (
            <span className="text-xs text-muted-foreground px-2 py-1">+{value.length - 3}</span>
          )}
        </div>
      );
    }

    if (criterion.isMetric) {
      return (
        <span className={`text-lg font-bold ${getMetricColor(Number(value), criterion.key)}`}>
          {value}{criterion.suffix || ''}
        </span>
      );
    }
    
    return (
      <span className="text-sm font-medium text-foreground">
        {value}{criterion.suffix || ''}
      </span>
    );
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-md"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className={`relative bg-card rounded-3xl shadow-2xl w-full max-w-6xl max-h-[85vh] overflow-hidden border border-border/50 transition-all duration-300 ${
          isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-xl border-b border-border/50 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg">
                <GitCompare className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {t('compare.title')}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {selectedSolutions.length} {t('compare.selected')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSolutions}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('compare.clearAll')}
              </Button>
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-auto max-h-[calc(85vh-100px)]">
          {selectedSolutions.length < 2 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
                <GitCompare className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                {t('compare.selectAtLeast')}
              </p>
            </div>
          ) : (
            <div className="p-6">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left p-4 font-medium text-muted-foreground text-sm sticky left-0 bg-card z-10 min-w-[140px]">
                      {t('compare.criteria')}
                    </th>
                    {fullSolutions?.map((solution) => (
                      <th key={solution.id} className="p-4 min-w-[220px]">
                        <div className="flex flex-col items-center gap-3">
                          <div className="relative group">
                            <button
                              onClick={() => removeSolution(solution.id)}
                              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-destructive/10 hover:bg-destructive hover:text-white flex items-center justify-center transition-all z-10 opacity-0 group-hover:opacity-100"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                            {solution.logo_url ? (
                              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary/10 to-secondary/10 p-2 flex items-center justify-center">
                                <img
                                  src={getLogoUrl(solution.logo_url)!}
                                  alt={solution.name}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                            ) : (
                              <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/70 rounded-xl flex items-center justify-center text-primary-foreground font-semibold text-lg">
                                AI
                              </div>
                            )}
                          </div>
                          <div className="text-center">
                            <div className="font-semibold text-foreground">
                              {solution.name}
                            </div>
                            <div className="text-xs text-primary font-medium">
                              {solution.ai_provider}
                            </div>
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {criteria.map((criterion, i) => (
                    <tr 
                      key={criterion.key}
                      className={`${i % 2 === 0 ? 'bg-muted/20' : ''} hover:bg-muted/40 transition-colors`}
                    >
                      <td className="p-4 font-medium text-sm text-foreground sticky left-0 bg-inherit z-10">
                        {criterion.label}
                      </td>
                      {fullSolutions?.map((solution) => (
                        <td key={solution.id} className="p-4 text-center">
                          {renderValue(solution, criterion)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
